(ns frontend.view.field-templates
  (:require
   [common.localization :as loc]
   [common.schema.assets :as assets]
   [common.schema.registry :as registry]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as state-permissions]
   [potpuri.core :as potpuri :refer [fn-> fn->>]]))

(defn valid-attribute?
  [attribute entity]
  (let [oppo-status (:oppo/status entity)
        froppo-status (:fundraising-opportunity/status entity)
        sales-oppo-status (:sales-opportunity/status entity)
        exit-oppo-status (:exit-opportunity/status entity)
        active-oppo? (= :oppo.status/ok oppo-status)
        passed-oppo? (= :oppo.status/passed oppo-status)
        hold-oppo? (= :oppo.status/on-hold oppo-status)
        portfolio-oppo? (= :oppo.status/portfolio oppo-status)
        won-froppo? (= :fundraising-opportunity.status/won froppo-status)
        lost-froppo? (= :fundraising-opportunity.status/lost froppo-status)
        hold-froppo? (= :fundraising-opportunity.status/on-hold froppo-status)
        lost-sales-oppo? (= :sales-opportunity.status/lost sales-oppo-status)
        hold-sales-oppo? (= :sales-opportunity.status/on-hold sales-oppo-status)
        won-sales-oppo? (= :sales-opportunity.status/won sales-oppo-status)
        lost-exit-oppo? (= :exit-opportunity.status/lost exit-oppo-status)
        hold-exit-oppo? (= :exit-opportunity.status/on-hold exit-oppo-status)
        won-exit-oppo? (= :exit-opportunity.status/won exit-oppo-status)]
    (case attribute
      :oppo/pass-reason-secondary passed-oppo?
      :oppo/pass-reason-v2 passed-oppo?
      :oppo/passed-on passed-oppo?
      :oppo/passed-by passed-oppo?
      :oppo/last-pass-comment passed-oppo?
      :oppo/on-hold-until hold-oppo?
      :oppo/put-on-hold-on hold-oppo?
      :oppo/put-on-hold-by hold-oppo?
      :oppo/last-hold-comment hold-oppo?
      :oppo/reactivated-on active-oppo?
      :oppo/reactivated-by active-oppo?
      :oppo/moved-to-portfolio-on portfolio-oppo?
      :oppo/moved-to-portfolio-by portfolio-oppo?
      :fundraising-opportunity/lost-reason lost-froppo?
      :fundraising-opportunity/lost-on lost-froppo?
      :fundraising-opportunity/lost-comment lost-froppo?
      :fundraising-opportunity/on-hold-comment hold-froppo?
      :fundraising-opportunity/on-hold-until hold-froppo?
      :fundraising-opportunity/put-on-hold-on hold-froppo?
      :fundraising-opportunity/won-on won-froppo?
      :sales-opportunity/lost-reason lost-sales-oppo?
      :sales-opportunity/lost-on lost-sales-oppo?
      :sales-opportunity/lost-comment lost-sales-oppo?
      :sales-opportunity/on-hold-comment hold-sales-oppo?
      :sales-opportunity/on-hold-until hold-sales-oppo?
      :sales-opportunity/put-on-hold-on hold-sales-oppo?
      :sales-opportunity/won-on won-sales-oppo?
      :exit-opportunity/lost-reason lost-exit-oppo?
      :exit-opportunity/lost-on lost-exit-oppo?
      :exit-opportunity/lost-comment lost-exit-oppo?
      :exit-opportunity/on-hold-comment hold-exit-oppo?
      :exit-opportunity/on-hold-until hold-exit-oppo?
      :exit-opportunity/put-on-hold-on hold-exit-oppo?
      :exit-opportunity/won-on won-exit-oppo?
      :exit-opportunity/set-as-won-by won-exit-oppo?
      :exit-opportunity/set-as-won-on won-exit-oppo?
      :exit-opportunity/set-as-lost-by lost-exit-oppo?
      :exit-opportunity/set-as-lost-on lost-exit-oppo?
      true)))

;; cljc-schema level solution needed?
;; -maybe explicit :field/editable? and :field/viewable? or similar
(def system-fields
  [:common/migration-note
   :common/tenant
   :common/owner-role
   :common/disabled?
   :common/zap-sample?
   :common/gift-sample?
   :common/related-entities
   :common/archived?
   :common/change-meta
   :common/ord
   :common/containing-entity
   :company/clearbit-company
   :company/fullcontact-company
   :company/intelligence-company
   :person/fullcontact-person
   :person/clearbit-person
   :person/intelligence-person
   :oppo/asset-type
   :oppo/asset-class
   :oppo/flagged-by
   :asset/type])

;;must differentiate labels such as
;; "Description" (sales-oppo),
;; "Customer (company) - Description",
;; "Legal entity (company) - Description" and
;; "Legal entity (investor) - Description"
;; for both the read-only header-fields and layout-editor field-selector
(defn customer-label
  [tenant-schema {:keys [attribute label]}]
  (let [attribute-label (or
                         (when-let [fs (registry/get-field-schema tenant-schema attribute)]
                           (loc/field-name attribute fs))
                         label
                         (loc/tr attribute)
                        )
        ns (namespace attribute)]
    (cond
      (contains? #{"common" "customer" "db"} ns)
      (str "Customer - " attribute-label)

      (= ns "investor")
      (str "Customer (investor) - " attribute-label)

      (contains? #{"company" "asset"} ns)
      (str "Customer (company) - " attribute-label)

      (= ns "person")
      (str "Customer (person) - " attribute-label)

      :else
      (str "Customer - " attribute-label))))

(defn legal-entity-label
  [{:keys [attribute label]}]
  (let [tenant-schema @session/tenant-schema
        attribute-label (or label
                            (let [fs (registry/get-field-schema tenant-schema attribute)]
                              (loc/field-name attribute fs)))
        ns (namespace attribute)]
    (cond
      (contains? #{"common" "customer" "db"} ns)
      (str "Legal entity - " attribute-label)

      (= ns "investor")
      (str "Legal entity (investor) - " attribute-label)

      (contains? #{"company" "asset"} ns)
      (str "Legal entity (company)- " attribute-label)

      (= ns "person")
      (str "Legal entity (person) - " attribute-label)

      :else
      (str "Legal entity - " attribute-label))))

(defn target-asset-label
  [{:keys [attribute label] :as fs}]
  (let [tenant-schema @session/tenant-schema
        attribute-label (or label
                            (let [fs (registry/get-field-schema tenant-schema attribute)]
                              (loc/field-name attribute fs)))
        ns (namespace attribute)]
    (cond
      (contains? #{"common" "asset" "db"} ns)
      (str "Target asset - " attribute-label)

      (= ns "company")
      (str "Target asset (company) - " attribute-label)

      (= ns "real-estate")
      (str "Target asset (real estate) - " attribute-label)

      (= ns "fof-asset")
      (str "Target asset (fund) - " attribute-label)

      (= ns "other-asset")
      (str "Target asset (generic asset) - " attribute-label)

      :else
      nil)))

(defn remove-from-nev-header
  []
  (concat system-fields
          [:common/attachments
           :company/logo]))

;; only one layer deep - no ref-attributes
(defn entity-type-template
  [{:keys [entity-type tenant-schema account role pipeline-id
           remove-fields selected-fields overrides read-only-fields]}]
  (let [entity-schema (registry/get-schema tenant-schema entity-type)
        ;; No 'remove-from-nev-header' fields by default. If fields are needed, add a parameter to override behavior:
        remove-all (concat remove-fields (remove-from-nev-header))
        entity-schema (apply dissoc entity-schema remove-all)
        selected-set (set selected-fields)]
    (->>
     entity-schema
     (filter (fn [[k v]]
               ;; already selected, but no access: we still need it for field-selector options:
               (or (contains? selected-set k)
                   (state-permissions/field-read-access? k entity-type pipeline-id))))
     (map (fn [[attribute field-schema]]
            (let [field-overrides (get overrides attribute)]
              [attribute
               (if (map? field-overrides)
                 (potpuri/deep-merge field-schema field-overrides)
                 field-schema)])))
     (keep (fn [[attribute field-schema]]
             (let [visibility (or (:editor-type field-schema)
                                  (:editor/type field-schema)
                                  (->> field-schema
                                       :field/default-visibility
                                       :editor))]
               [attribute
                (merge
                 field-schema
                 {:attribute   attribute
                  :access/type (when-let [at (state-permissions/access-to-field-long attribute
                                                                                     entity-type
                                                                                     pipeline-id)]
                                 (if (some #{attribute} read-only-fields)
                                   :field.access/read
                                   at))
                  :editor/type visibility
                  :editor-type visibility})]))))))

(defn customer-template
  [args]
  (let [tenant-schema @session/tenant-schema]
    (->> [:entity.type/person :entity.type/company :entity.type/investor]
         (mapcat (fn [entity-type]
                   (entity-type-template (assoc args :entity-type entity-type))))
         (into {})
         (sort-by (fn [[k {:keys [label]}]]
                    (or label
                        (when-let [fs (registry/get-field-schema tenant-schema k)]
                          (loc/field-name k fs))))))))

(defn legal-entity-template
  [args]
  (let [tenant-schema @session/tenant-schema]
    (->> [:entity.type/person :entity.type/company]
         (mapcat (fn [entity-type]
                   (entity-type-template (assoc args :entity-type entity-type))))
         (into {})
         (sort-by (fn [[k {:keys [label]}]]
                    (or label
                        (when-let [fs (registry/get-field-schema tenant-schema k)]
                          (loc/field-name k fs))))))))

(defn target-asset-template
  [containing-entity-type args]
  ;; header field selections not pipeline specific => no pipeline filtering:
  (let [tenant-schema @session/tenant-schema]
    (->> assets/target-asset-type-mappings
         containing-entity-type
         (filter session/asset-type-feature?)
         (mapcat (fn [entity-type]
                   (entity-type-template (assoc args :entity-type entity-type))))
         (into {})
         (sort-by (fn [[k {:keys [label]}]]
                    (or label
                        (when-let [fs (registry/get-field-schema tenant-schema k)]
                          (loc/field-name k fs))))))))

(def oppo-target-asset-template (partial target-asset-template :entity.type/oppo))
(def holding-target-asset-template (partial target-asset-template :entity.type/holding))

;;update field-schema according to a specific entity
;; (i.e. handle company-attributes when legal-entity is a person etc)
(defn entity-field-schema
  [{:keys [attribute field-schema entity pipeline-id read-only-fields remove-fields]}]
  (let [visibility (or #_(get override-editor-types attribute)
                       (->> field-schema
                            :field/default-visibility
                            :editor))
        ;;'attribute' might be ref-attribute (i.e. a vector)
        kw-attribute (:attribute field-schema)
        access?
        (state-permissions/field-read-access? kw-attribute (:entity/type entity) pipeline-id)
        hide? (or (not access?)
                  (some #{attribute} remove-fields))]
    (merge field-schema
           {:entity-id   (:db/id entity)
            :access/type (when-let [at (state-permissions/access-to-field-long (:attribute
                                                                                field-schema)
                                                                               (:entity/type entity)
                                                                               pipeline-id)]

                           (if (some #{attribute} read-only-fields)
                             :field.access/read
                             at))}
           (when hide?
             {:read-only? true})
           (when (fn? (:field/custom-params field-schema))
             ((:field/custom-params field-schema) entity)))))
